import { Content } from '@/types/content';
import { findAllOfType, findFirstOfType } from 'lib/labrador/content-operations/findBy';
import { mergeDefined } from 'lib/utils';
import { stripHtml } from 'lib/utils/stripHtml';
import { NewsArticle, WithContext } from 'schema-dts';
import { isTruthy } from 'typesafe-utils';
import {
  CustomSchemaMarkupConfig,
  correctionsPolicy,
  getBylineUrl,
  getFormattedDate,
  getFormattedDateFromUnix,
  getImageSrc,
  getPublishedUrl,
  missionCoveragePrioritesPolicy,
  publisher,
  publishingPrinciples,
  sameAs,
  unnamedSourcesPolicy,
  verificationFactCheckingPolicy,
} from './utils';

export const getArticlePageSchema = (
  pageData: Content,
  schema?: Partial<NewsArticle & CustomSchemaMarkupConfig>
): WithContext<NewsArticle & CustomSchemaMarkupConfig> => {
  const { data, children } = pageData;
  const { publishedUrl, publishedDatetime, modifiedDatetime, hasAffiliate, category, tags } = data ?? {};

  const url = getPublishedUrl(publishedUrl);

  const articleHeaderContent = findFirstOfType(children, 'articleHeader');
  const articleHeaderImageContent = findFirstOfType(articleHeaderContent?.children ?? [], 'labradorImage');
  const imageId = articleHeaderImageContent?.data?.imageId;
  const articleHeaderImageSrc = imageId ? getImageSrc(imageId, 1200, 675) : '';
  const thumbnailUrl = imageId ? getImageSrc(imageId, 800, 450) : '';

  const datePublished = getFormattedDate(publishedDatetime);
  const dateModified = getFormattedDateFromUnix(modifiedDatetime);

  const articleBylines = articleHeaderContent ? findAllOfType(articleHeaderContent.children, 'byline') : [];
  const bylineNames = articleBylines.map((byline) => `${byline.data.firstName || ''} ${byline.data.lastName || ''}`);

  const extraTags = [category?.includes('nyheter') && 'senaste-nytt', hasAffiliate && 'alleraffiliate'];
  const allTags = [...tags, ...extraTags].filter(isTruthy);

  const articlePageSchema: Partial<WithContext<NewsArticle & CustomSchemaMarkupConfig>> = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    isBasedOn: pageData.data.canonical || '',
    headline: stripHtml(pageData.data.title || ''),
    url,
    thumbnailUrl,
    image: {
      '@type': 'ImageObject',
      url: articleHeaderImageSrc,
    },
    datePublished,
    dateModified,
    articleSection: category || '',
    author: bylineNames.map((byline) => ({
      '@type': 'Person',
      name: byline,
      url: getBylineUrl(byline),
    })),
    creator: bylineNames,
    keywords: allTags,
    identifier: pageData.meta.uniqueId,
    publisher,
    sameAs,
    publishingPrinciples,
    verificationFactCheckingPolicy,
    correctionsPolicy,
    unnamedSourcesPolicy,
    missionCoveragePrioritesPolicy,
  };

  return mergeDefined(articlePageSchema, schema);
};
